import React, { useEffect, useRef, useState } from 'react';
import { mediaApi } from '../api/media';
import { getErrorMessage } from '../api/client';
import Spinner from './ui/Spinner';
import Alert from './ui/Alert';
import { IconPlus, IconTrash } from './ui/Icons';

export default function GalleryUploader({ eventId, max = 6 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await mediaApi.list(eventId);
      setItems(data.items || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const remaining = Math.max(0, max - items.length);

  const handleFiles = async (files) => {
    setError('');
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length === 0) return;

    setUploading(true);
    try {
      for (const file of toUpload) {
        setProgress(0);
        const media = await mediaApi.upload(eventId, file, setProgress);
        setItems((prev) => [...prev, media]);
      }
      setProgress(0);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('¿Eliminar esta foto?')) return;
    try {
      await mediaApi.remove(eventId, mediaId);
      setItems((prev) => prev.filter((m) => m.id !== mediaId));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-800">Galería de fotos</h3>
          <p className="text-xs text-slate-500">
            {items.length} de {max} fotos — aparecerán en la invitación
          </p>
        </div>
        {remaining > 0 && (
          <span className="text-xs font-semibold text-brand-600 bg-brand-50 px-3 py-1.5 rounded-full">
            Te quedan {remaining}
          </span>
        )}
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="grid place-items-center py-10">
          <Spinner className="text-brand-500" />
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {items.map((item) => (
            <div
              key={item.id}
              className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group"
            >
              <img
                src={item.url}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => handleDelete(item.id)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
                title="Eliminar"
              >
                <span className="block w-3.5 h-3.5">
                  <IconTrash className="w-full h-full" />
                </span>
              </button>
            </div>
          ))}

          {remaining > 0 && (
            <label
              className={`relative aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-brand-500 hover:bg-brand-50 transition cursor-pointer grid place-items-center ${
                uploading ? 'pointer-events-none opacity-60' : ''
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
              {uploading ? (
                <div className="text-center px-2">
                  <Spinner size="sm" className="text-brand-500 mx-auto mb-1" />
                  <p className="text-xs font-semibold text-brand-600">{progress}%</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-6 h-6 mx-auto text-slate-400 mb-2">
                    <IconPlus className="w-full h-full" />
                  </div>
                  <p className="text-xs font-semibold text-slate-500">Agregar</p>
                </div>
              )}
            </label>
          )}
        </div>
      )}

      <p className="text-xs text-slate-400">
        JPG, PNG o WebP · máx 8 MB por foto · máximo {max} fotos
      </p>
    </div>
  );
}